import React, { forwardRef, type ReactNode, useEffect } from 'react';

type SeedGenerator = (id: any) => string;

/**
 * Returns a unique id using `React.useId()` with a selector-safe replacement to match
 * React 19 functionality.
 * @see https://github.com/facebook/react/pull/33422
 *
 * Generally, you should just use `React.useId()` directly. Use this function if you
 * need the generated id in an HTML selector, as `React.useId()` does not generate
 * selector-safe ids. Using these identifiers in selectors should be avoided, even
 * in tests.
 */
export function useId(): string {
	// React uses `:` or `«»` in selectors which breaks `document.querySelector(…)`.
	// Replace with `_` to match future React 19 functionality.
	return React.useId().replace(/[:«»]/g, '_');
}

export interface IdProviderProps {
	/**
	 * Children to render.
	 * Maybe a function that receives the unique id as an argument.
	 */
	children: ReactNode | ((props: { id: string }) => ReactNode);
	/**
	 * A prefix to be added to the generated id.
	 */
	prefix?: string;
	/**
	 * A postfix to be added to the generated id.
	 */
	postfix?: string;
}

/**
 * A wrapper component that allows access to an id generated by useId within a class component.
 *
 * This component is only intended for class components, function components
 * should probably use `React.useId()` directly.
 *
 * @private
 * @deprecated This import shouldn't be used, suggested to use `React.useId()` directly. It is kept for class component compatibility.
 */
export const IdProvider: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<IdProviderProps> & React.RefAttributes<string>
> = forwardRef<string, IdProviderProps>(({ children, postfix = '', prefix = '' }, ref) => {
	const id = `${prefix}${useId()}${postfix}`;

	useEffect(() => {
		if (!ref) {
			return;
		}
		switch (typeof ref) {
			case 'function':
				ref(id);
				break;
			case 'object':
				ref.current = id;
				break;
			default:
				throw new Error(`Unreachable case for unsupported type of ref "${typeof ref}"`);
		}
	}, [id, ref]);

	return <>{typeof children === 'function' ? children({ id }) : children}</>;
});

/**
 * Returns an id generator
 *
 * @private
 * @deprecated This import shouldn't be used, suggested to use `React.useId()` directly.
 */
export function useIdSeed(): SeedGenerator {
	const uid = useId();
	return (id: any) => `${uid}-${id.toString()}`;
}
